import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { flexRender } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';

// ASSETS
import RoundedWarningIcon from 'assets/RoundedWarningIcon';

// CONSTANTS
import { DevicelessTableTab } from 'constants/telematics';

// COMPONENTS
import Search from 'components/Search';
import {
  EmptyState,
  EmptyStateText,
  LoadingState,
  SortableHeader,
  Sorting,
  TableContainer,
} from 'components/SectionTable';
import { EnrollmentButton } from './Enrollment.modal';
import { SimpleLoading, Spinner } from 'components/Loading';
import { TableTabs } from '../../components';

// HOOKS
import { useAppDispatch } from 'hooks/store';
import useDevicelessTable from './Table.hook';

// SERVICES
import { listEnrolments } from 'services/Telematics/thunks';

// TYPES
import { DataSources } from 'services/resources';

// STYLES
import colors from 'styles/colors';
import { SearchContainer } from '../../../styles';
import { HighlightRow } from '../../styles';
import { TableToolsContainer } from '../styles';
import { DataSourceEmptyState, LoadingContainer } from './styles';

type OEMTelematicsTableProps = {
  dataSources: DataSources;
  dataSourcesLoading: boolean;
  onlyDataSourceIsZubieOEM: boolean;
};

const OEMTelematicsTable = ({ dataSources, dataSourcesLoading, onlyDataSourceIsZubieOEM }: OEMTelematicsTableProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { state } = useLocation<{ highlightVehicle?: number }>();

  useEffect(() => {
    dispatch(listEnrolments());
  }, [dispatch]);

  const {
    dataLoading,
    rows,
    virtualRows,
    totalSize,
    globalFilter,
    setGlobalFilter,
    tableContainerRef,
    table,
    handleSelectedTab,
    activeTab,
    activeCount,
    pendingCount,
    failedCount,
  } = useDevicelessTable();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const tabsInfo = [
    {
      label: 'views.FleetSettings.OEMTelematics.Table.active',
      count: activeCount,
      onClick: () => handleSelectedTab(DevicelessTableTab.Active),
      $selected: activeTab === DevicelessTableTab.Active,
    },
    {
      label: 'views.FleetSettings.OEMTelematics.Table.pending',
      count: pendingCount,
      onClick: () => handleSelectedTab(DevicelessTableTab.Pending),
      $selected: activeTab === DevicelessTableTab.Pending,
      disabled: pendingCount === 0,
    },
    {
      label: 'views.FleetSettings.OEMTelematics.Table.failed',
      count: failedCount,
      onClick: () => handleSelectedTab(DevicelessTableTab.Failed),
      $selected: activeTab === DevicelessTableTab.Failed,
      disabled: failedCount === 0,
    },
  ];

  return (
    <>
      {dataSourcesLoading && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}
      {!dataSourcesLoading && dataSources.length > 0 && !onlyDataSourceIsZubieOEM && (
        <>
          <TableToolsContainer>
            <TableTabs tabs={tabsInfo} />
            <SearchContainer>
              <Search
                type="text"
                searchQuery={globalFilter}
                onChangeSearchQuery={(value) => setGlobalFilter(value)}
                placeholder={formatMessage({
                  id: 'views.FleetSettings.OEMTelematics.Table.searchPlaceholder',
                })}
              />
            </SearchContainer>
          </TableToolsContainer>
          <TableContainer ref={tableContainerRef}>
            {!dataLoading && rows.length > 0 && (
              <table className="table">
                <thead className="thead">
                  {
                    // Loop over the header rows
                    table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const isSorted = header.column.getIsSorted();
                          const canSort = header.column.getCanSort();
                          return (
                            <SortableHeader
                              key={header.id}
                              $sortable={canSort}
                              onClick={header.column.getToggleSortingHandler()}
                              style={{
                                width: header.getSize(),
                              }}
                            >
                              {header.isPlaceholder || header.id === 'actions' ? null : (
                                <div>
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                  <Sorting canSort={canSort} isSorted={isSorted} />
                                </div>
                              )}
                            </SortableHeader>
                          );
                        })}
                      </tr>
                    ))
                  }
                </thead>
                <tbody className="tbody">
                  {paddingTop > 0 && (
                    <tr>
                      <td style={{ height: `${paddingTop}px` }} />
                    </tr>
                  )}
                  {virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];

                    return (
                      <HighlightRow key={row.id} isSelected={row.original.vehicle.id === state?.highlightVehicle}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              width: cell.column.getSize(),
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </HighlightRow>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr>
                      <td style={{ height: `${paddingBottom}px` }} />
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {!dataLoading && rows.length === 0 && (
              <EmptyState placeholder={formatMessage({ id: 'views.FleetSettings.OEMTelematics.noVehiclesEnrolled' })} />
            )}
            {dataLoading && (
              <LoadingState>
                <SimpleLoading color={colors.fleetBlue} />
              </LoadingState>
            )}
          </TableContainer>
          {dataSources.length > 0 && <EnrollmentButton />}
        </>
      )}
      {!dataSourcesLoading && (dataSources.length === 0 || onlyDataSourceIsZubieOEM) && (
        <DataSourceEmptyState>
          <RoundedWarningIcon color={colors.orangeDarker} style={{ flexShrink: 0 }} />
          <EmptyStateText>{formatMessage({ id: 'views.FleetSettings.OEMTelematics.noDatasource' })}</EmptyStateText>
        </DataSourceEmptyState>
      )}
    </>
  );
};

export default OEMTelematicsTable;
